// src/components/UsersManagement.js
import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import Papa from "papaparse";
import "./UsersManagement.css";

const USER_TYPE_LABELS = {
  migrante_refugiado: 'Migrante / Refugiado(a)',
  representante_organizacao: 'Representante de organização',
  apoiador: 'Apoiador(a)',
};

const STATUS_LABELS = {
  ativo: 'Ativo',
  em_pausa: 'Em pausa',
  nao_disponivel: 'Não disponível',
};

const SITUATION_LABELS = {
  refugiado: 'Refugiado(a) reconhecido(a)',
  solicitante_refugio: 'Solicitante de refúgio',
  migrante: 'Migrante',
  apatrida: 'Apátrida',
  outro: 'Outro',
};

const formatCreatedAt = (value) => {
  if (!value) return '';
  let ms = null;
  if (value.toMillis) ms = value.toMillis();
  else if (value.seconds) ms = value.seconds * 1000;
  else {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) ms = parsed.getTime();
  }
  if (ms === null) return '';
  return new Date(ms).toLocaleDateString('pt-BR');
};

const addressFieldOf = (user, key) => {
  if (user.address && typeof user.address === 'object' && user.address[key]) {
    return user.address[key];
  }
  return user[key] || '';
};

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [editUserId, setEditUserId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    const fetchUsers = async () => {
      const userCollectionRef = collection(db, "users");
      const userData = await getDocs(userCollectionRef);
      setUsers(userData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    };

    fetchUsers();
  }, []);

  const handleVerifiedChange = (event) => {
    setEditFormData({ ...editFormData, verified: event.target.checked });
  };

  const handleEditClick = (user) => {
    setEditUserId(user.id);
    setEditFormData(user);
  };

  const handleFormChange = (event) => {
    setEditFormData({
      ...editFormData,
      [event.target.name]: event.target.value,
    });
  };

  const handleCancelEdit = () => {
    setEditUserId(null);
  };

  const handleUpdate = async () => {
    const userDocRef = doc(db, "users", editUserId);
    await updateDoc(userDocRef, editFormData);
    setUsers(
      users.map((user) =>
        user.id === editUserId ? { ...user, ...editFormData } : user
      )
    );
    setEditUserId(null);
  };

  const handleDelete = async (userId) => {
    if (
      window.confirm(
        "Você tem certeza que deseja deletar este usuário? Esta ação é irreversível."
      )
    ) {
      try {
        const userDocRef = doc(db, "users", userId);
        await deleteDoc(userDocRef);
        setUsers(users.filter((user) => user.id !== userId));
      } catch (error) {
        console.error("Error deleting user: ", error);
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExport = () => {
    const exportData = users.map((user) => {
      const country = addressFieldOf(user, 'country');
      const state = addressFieldOf(user, 'state');
      const city = addressFieldOf(user, 'city');
      const neighborhood = addressFieldOf(user, 'neighborhood');
      const address = addressFieldOf(user, 'address');
      const cep = addressFieldOf(user, 'zipCode');
      return {
        Nome: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        Email: user.email || '',
        Celular: user.mobile || '',
        'Tipo de usuário': USER_TYPE_LABELS[user.userType] || '',
        Status: STATUS_LABELS[user.status || 'ativo'] || '',
        Nacionalidade: user.nationality || '',
        Situação: SITUATION_LABELS[user.situation] || user.situation || '',
        'Data de Cadastro': formatCreatedAt(user.createdAt),
        'Data de Nascimento': user.birthDate || '',
        País: country,
        Estado: state,
        Cidade: city,
        Bairro: neighborhood,
        Endereço: address,
        CEP: cep,
        Instituição: user.institutionName || '',
        CNPJ: user.cnpj || '',
      };
    });
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "users_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = `${user.firstName || ''} ${user.lastName || ''}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const userStatus = user.status || 'ativo';
    const matchesStatus = !statusFilter || userStatus === statusFilter;
    const matchesUserType = !userTypeFilter || user.userType === userTypeFilter;
    return matchesSearch && matchesStatus && matchesUserType;
  });

  return (
    <div className="user-management">
      <h2>User Management</h2>
      {editUserId ? (
        <div className="edit-form">
          <label htmlFor="verified">
            <input
              type="checkbox"
              name="verified"
              checked={editFormData.verified || false}
              onChange={handleVerifiedChange}
            />{" "}
            Verified
          </label>
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={editFormData.firstName || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={editFormData.lastName || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="email"
            placeholder="Email"
            value={editFormData.email || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="mobile"
            placeholder="Mobile"
            value={editFormData.mobile || ""}
            onChange={handleFormChange}
          />
          <input
            type="date"
            name="birthDate"
            placeholder="Birth Date"
            value={editFormData.birthDate || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="address"
            placeholder="Address"
            value={editFormData.address || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="city"
            placeholder="City"
            value={editFormData.city || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="state"
            placeholder="State"
            value={editFormData.state || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="country"
            placeholder="Country"
            value={editFormData.country || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="zipCode"
            placeholder="Zip Code"
            value={editFormData.zipCode || ""}
            onChange={handleFormChange}
          />
          <input
            type="text"
            name="avatar"
            placeholder="Avatar URL"
            value={editFormData.avatar || ""}
            onChange={handleFormChange}
          />
          <select
            name="userType"
            value={editFormData.userType || ""}
            onChange={handleFormChange}
          >
            <option value="">Tipo de usuário (não definido)</option>
            {Object.entries(USER_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            name="status"
            value={editFormData.status || "ativo"}
            onChange={handleFormChange}
          >
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button onClick={handleUpdate}>Salvar</button>
          <button className="voltar" onClick={handleCancelEdit}>
            Voltar
          </button>
        </div>
      ) : (
        <>
          <div className="controls">
            <input
              type="text"
              placeholder="Procurar por nome"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="search-input"
            >
              <option value="">Todos os tipos</option>
              {Object.entries(USER_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="search-input"
            >
              <option value="">Todos os status</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button className="export-button" onClick={handleExport}>
              Exportar usuários
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[1100px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">Nome</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Tipo</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Nacionalidade</th>
                  <th className="p-2 text-left">Data de Cadastro</th>
                  <th className="p-2 text-left">País</th>
                  <th className="p-2 text-left">Estado</th>
                  <th className="p-2 text-left">Cidade</th>
                  <th className="p-2 text-left">Bairro</th>
                  <th className="p-2 text-left">Celular</th>
                  <th className="p-2 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{user.firstName} {user.lastName}</td>
                    <td className="p-2">{user.email || '-'}</td>
                    <td className="p-2">{USER_TYPE_LABELS[user.userType] || '-'}</td>
                    <td className="p-2">{STATUS_LABELS[user.status || 'ativo']}</td>
                    <td className="p-2">{user.nationality || '-'}</td>
                    <td className="p-2">{formatCreatedAt(user.createdAt) || '-'}</td>
                    <td className="p-2">{addressFieldOf(user, 'country') || '-'}</td>
                    <td className="p-2">{addressFieldOf(user, 'state') || '-'}</td>
                    <td className="p-2">{addressFieldOf(user, 'city') || '-'}</td>
                    <td className="p-2">{addressFieldOf(user, 'neighborhood') || '-'}</td>
                    <td className="p-2">{user.mobile || '-'}</td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                          onClick={() => handleDelete(user.id)}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default UsersManagement;
